import { isQualifiedEvidence, type ExerciseAttempt } from '@/entities/attempt'
import type { AbilityDimension } from '@/entities/question/types'
import type { StudentLearningProfile } from './types'
import { getAttemptProfileDimensions } from './createDynamicAssessmentSnapshot'

const abilityDimensions: AbilityDimension[] = [
  'listening', 'speaking', 'reading', 'writing', 'vocabulary', 'grammar', 'phonics',
]
const recentQuestionLimit = 25

export interface AbilityGrowthDimensionSnapshot {
  points: number
  questionCount: number
  independentCorrectCount: number
  supportedCorrectCount: number
  firstWrongCount: number
  correctedCount: number
}

export interface AbilityGrowthSnapshot {
  abilities: Record<AbilityDimension, AbilityGrowthDimensionSnapshot>
}

interface ScoredQuestion {
  dimensions: AbilityDimension[]
  completedAt: string
  points: number
  independentCorrect: boolean
  supportedCorrect: boolean
  firstWrong: boolean
  corrected: boolean
}

function normalizeQuestionIdentityPart(value: unknown): string {
  if (value === null || value === undefined) return ''
  return String(value)
    .normalize('NFKC')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
}

function questionIdentity(attempt: ExerciseAttempt): string {
  const question = normalizeQuestionIdentityPart(attempt.questionSnapshot)
  const answer = normalizeQuestionIdentityPart(attempt.correctAnswer)
  if (question) return `content:${question}:${answer}`
  if (attempt.contentFingerprint) return `fingerprint:${attempt.contentFingerprint}`
  return `exercise:${attempt.exerciseId}`
}

function learningQuestionKey(attempt: ExerciseAttempt): string {
  return [
    attempt.studentId,
    attempt.stageId ?? '',
    attempt.unitId ?? '',
    questionIdentity(attempt),
  ].join(':')
}

function scoreQuestion(attempts: ExerciseAttempt[]): ScoredQuestion {
  const ordered = [...attempts].sort((left, right) => (
    new Date(left.completedAt).getTime() - new Date(right.completedAt).getTime()
  ))
  const first = ordered[0]
  const independentCorrect = first.correct && first.hintCount === 0 && first.retryCount === 0
  const supportedCorrect = first.correct && !independentCorrect
  const firstWrong = !first.correct
  const corrected = firstWrong && ordered.slice(1).some((attempt) => attempt.correct)

  return {
    dimensions: getAttemptProfileDimensions(first),
    completedAt: first.completedAt,
    points: independentCorrect ? 2 : supportedCorrect ? 1 : corrected ? -1 : -2,
    independentCorrect,
    supportedCorrect,
    firstWrong,
    corrected,
  }
}

function emptyDimension(): AbilityGrowthDimensionSnapshot {
  return {
    points: 0,
    questionCount: 0,
    independentCorrectCount: 0,
    supportedCorrectCount: 0,
    firstWrongCount: 0,
    correctedCount: 0,
  }
}

export function createAbilityGrowthSnapshot(
  profile: StudentLearningProfile,
  attempts: readonly ExerciseAttempt[],
): AbilityGrowthSnapshot {
  const baselineTime = new Date(profile.updatedAt).getTime()
  const grouped = new Map<string, ExerciseAttempt[]>()

  for (const attempt of attempts) {
    const completedAt = new Date(attempt.completedAt).getTime()
    if (attempt.studentId !== profile.identity.id
      || !isQualifiedEvidence(attempt)
      || attempt.evidenceKind === 'remediation-training'
      || !Number.isFinite(completedAt)
      || completedAt <= baselineTime) continue
    const key = learningQuestionKey(attempt)
    grouped.set(key, [...(grouped.get(key) ?? []), attempt])
  }

  const questions = [...grouped.values()].map(scoreQuestion)
  const abilities = Object.fromEntries(abilityDimensions.map((dimension) => {
    const recent = questions
      .filter((question) => question.dimensions.includes(dimension))
      .sort((left, right) => new Date(left.completedAt).getTime() - new Date(right.completedAt).getTime())
      .slice(-recentQuestionLimit)

    return [dimension, recent.reduce<AbilityGrowthDimensionSnapshot>((summary, question) => ({
      points: summary.points + question.points,
      questionCount: summary.questionCount + 1,
      independentCorrectCount: summary.independentCorrectCount + Number(question.independentCorrect),
      supportedCorrectCount: summary.supportedCorrectCount + Number(question.supportedCorrect),
      firstWrongCount: summary.firstWrongCount + Number(question.firstWrong),
      correctedCount: summary.correctedCount + Number(question.corrected),
    }), emptyDimension())]
  })) as Record<AbilityDimension, AbilityGrowthDimensionSnapshot>

  return { abilities }
}
